import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobInputCheckbox,
  KoobLoader,
  KoobSearchBar,
  KoobTable,
  KoobTableBody,
  KoobTableCell,
  KoobTableHead,
  KoobTableHeader,
  KoobTableRow,
  KoobTableToolbar,
  KoobTabsCompact,
} from '@koob/ui';
import { NotificationChannelEnum, NotificationGroupEnum } from '@koob/enums';

const ALL = 'all';

export default function NotificationPreferencesEditor({
  preferences,
  setPreferences,
  loading = false,
}) {
  const { t } = useTranslation('misc');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState(ALL);
  const query = search.trim().toLowerCase();
  const shown = preferences.filter(
    (preference) =>
      (group === ALL || preference.group === group) &&
      (!query || preference.label?.toLowerCase().includes(query)),
  );

  // One tab per category the user has preferences in, "All" first.
  const tabs = [
    { id: ALL, label: t('notificationPreferences.all') },
    ...Object.values(NotificationGroupEnum)
      .filter((id) => preferences.some((preference) => preference.group === id))
      .map((id) => ({ id, label: t(`notificationPreferences.groups.${id}`) })),
  ];

  const handleCheckboxChange = (preference, channel, checked) => {
    const updatedPreferences = preferences.map((pref) => {
      if (pref.type === preference.type) {
        return { ...pref, [channel]: checked };
      }
      return pref;
    });
    setPreferences(updatedPreferences);
  };

  return (
    <div>
      {!loading ? (
        <KoobTable
          headerBar={
            <>
              <div className="overflow-x-auto whitespace-nowrap border-0 border-b border-solid border-k-gray px-5 pt-4">
                <KoobTabsCompact
                  tabs={tabs}
                  value={group}
                  onValueChange={setGroup}
                />
              </div>
              <KoobTableToolbar
                search={
                  <KoobSearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder={t('notificationPreferences.search')}
                  />
                }
              />
            </>
          }
        >
          <KoobTableHeader>
            <KoobTableRow>
              <KoobTableHead>Notification Type</KoobTableHead>
              <KoobTableHead align="center">Email</KoobTableHead>
              <KoobTableHead align="center">In-App</KoobTableHead>
            </KoobTableRow>
          </KoobTableHeader>

          <KoobTableBody>
            {shown.length === 0 && (
              <KoobTableRow>
                <KoobTableCell colSpan={3}>
                  <p className="m-0 py-6 text-center text-sm text-k-p">
                    {t('notificationPreferences.noMatch')}
                  </p>
                </KoobTableCell>
              </KoobTableRow>
            )}
            {shown.map((preference) => (
              <KoobTableRow key={preference.type}>
                <KoobTableCell>{preference.label}</KoobTableCell>

                <KoobTableCell>
                  <div className="flex justify-center">
                    <KoobInputCheckbox
                      checked={
                        preference.channelMail &&
                        preference.availableChannels.includes(
                          NotificationChannelEnum.MAIL,
                        )
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(preference, 'channelMail', checked)
                      }
                      disabled={
                        !preference.availableChannels.includes(
                          NotificationChannelEnum.MAIL,
                        )
                      }
                    />
                  </div>
                </KoobTableCell>

                <KoobTableCell>
                  <div className="flex justify-center">
                    <KoobInputCheckbox
                      checked={
                        preference.channelDatabase &&
                        preference.availableChannels.includes(
                          NotificationChannelEnum.DATABASE,
                        )
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(
                          preference,
                          'channelDatabase',
                          checked,
                        )
                      }
                      disabled={
                        !preference.availableChannels.includes(
                          NotificationChannelEnum.DATABASE,
                        )
                      }
                    />
                  </div>
                </KoobTableCell>
              </KoobTableRow>
            ))}
          </KoobTableBody>
        </KoobTable>
      ) : (
        <div className="flex justify-center items-center h-64">
          <KoobLoader />
        </div>
      )}
    </div>
  );
}
